import { DEFAULT_LOCALE, isSupportedLocale, type FeatureKey } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Ban, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { onApiMutation } from '@/lib/api-client';
import { subscribeToEventStream } from '@/lib/event-stream';
import { featureCatalogueQueryKey, useEffectiveFeatureCatalogue } from './api/use-features';
import {
  FeatureAvailabilityContext,
  useFeatureAvailability,
  type FeatureAvailabilityContextValue,
} from './feature-context';
import { featureKeysForPath, isFeatureConfigurationPath } from './lib/route-matching';

const FEATURE_CHANNEL = 'erp-feature-availability-v1';
const FEATURE_STORAGE_EVENT = 'erp.feature-availability.changed';

function FeatureLoadState({ retry }: { retry: () => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 p-6" role="alert">
      <Card>
        <CardHeader>
          <CardTitle>{t('errors.requestFailed')}</CardTitle>
          <CardDescription>{t('errors.unexpectedMessage')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button type="button" variant="outline" onClick={retry}>
            <RefreshCw aria-hidden="true" className="mr-2 size-4" />
            {t('common.retry')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export function FeatureAvailabilityProvider({ children }: { children: ReactNode }): JSX.Element {
  const query = useEffectiveFeatureCatalogue();
  const queryClient = useQueryClient();
  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: featureCatalogueQueryKey });
  }, [queryClient]);

  useEffect(() => {
    const channel =
      typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(FEATURE_CHANNEL);
    if (channel !== null) channel.onmessage = invalidate;
    const storage = (event: StorageEvent): void => {
      if (event.key === FEATURE_STORAGE_EVENT) invalidate();
    };
    window.addEventListener('storage', storage);
    const stopMutationListener = onApiMutation((path) => {
      if (!path.startsWith('/api/v1/admin/features')) return;
      invalidate();
      channel?.postMessage({ type: 'changed' });
      try {
        localStorage.setItem(FEATURE_STORAGE_EVENT, new Date().toISOString());
      } catch {
        // The same-tab invalidation and server stream remain available.
      }
    });
    const stopStream = subscribeToEventStream({
      path: '/api/v1/features/stream',
      events: ['feature-availability'],
      onEvent: invalidate,
    });
    return () => {
      stopMutationListener();
      stopStream();
      window.removeEventListener('storage', storage);
      channel?.close();
    };
  }, [invalidate]);

  const value = useMemo<FeatureAvailabilityContextValue | null>(() => {
    if (query.data === undefined) return null;
    const byKey = new Map(query.data.features.map((feature) => [feature.key, feature]));
    const isEnabled = (key: FeatureKey): boolean => byKey.get(key)?.effectiveEnabled === true;
    const keysForPath = (pathname: string): readonly FeatureKey[] =>
      featureKeysForPath(query.data, pathname);
    return {
      catalogue: query.data,
      isEnabled,
      keysForPath,
      isPathEnabled: (pathname: string) => {
        const keys = keysForPath(pathname);
        return keys.length === 0 || keys.some(isEnabled);
      },
    };
  }, [query.data]);

  if (query.isError) return <FeatureLoadState retry={() => void query.refetch()} />;
  if (value === null)
    return (
      <div className="space-y-4 p-6" aria-busy="true">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  return (
    <FeatureAvailabilityContext.Provider value={value}>
      {children}
    </FeatureAvailabilityContext.Provider>
  );
}

export function FeatureRouteBoundary({ children }: { children: ReactNode }): JSX.Element {
  const { i18n } = useTranslation();
  const location = useLocation();
  const availability = useFeatureAvailability();
  const keys = availability.keysForPath(location.pathname);
  const disabled = keys
    .map((key) => availability.catalogue.features.find((feature) => feature.key === key))
    .filter((feature) => feature !== undefined && !feature.effectiveEnabled);
  if (
    keys.length === 0 ||
    keys.some(availability.isEnabled) ||
    disabled.some(
      (feature) =>
        feature !== undefined &&
        [
          'complaints_returns',
          'scanner_picking',
          'full_inventory',
          'delivery_tours',
          'reusable_containers',
          'electronic_invoices',
        ].includes(feature.key) &&
        feature.shutdownMode === 'read-only-history',
    ) ||
    isFeatureConfigurationPath(availability.catalogue, location.pathname)
  ) {
    return <>{children}</>;
  }

  const resolved = i18n.resolvedLanguage ?? i18n.language;
  const locale = isSupportedLocale(resolved) ? resolved : DEFAULT_LOCALE;
  const feature = disabled[0];
  const title = feature?.name[locale] ?? 'Feature unavailable';
  const description = feature?.description[locale] ?? '';
  const copy = {
    de: 'Diese Funktion ist für Ihren Mandanten deaktiviert.',
    en: 'This feature is disabled for your tenant.',
    tr: 'Bu özellik kiracınız için devre dışıdır.',
    fr: 'Cette fonctionnalité est désactivée pour votre organisation.',
    es: 'Esta función está desactivada para su organización.',
  }[locale];
  return (
    <Card role="status" className="mx-auto max-w-2xl">
      <CardHeader>
        <Ban aria-hidden="true" className="mb-2 size-8 text-muted-foreground" />
        <CardTitle>{title}</CardTitle>
        <CardDescription>{copy}</CardDescription>
      </CardHeader>
      {description === '' ? null : <CardContent>{description}</CardContent>}
    </Card>
  );
}
